// src/components/ProductDetailPage/ProductDetailPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Image,
  Tab,
  Nav,
  Breadcrumb,
  Modal,
  Button,
  Row,
  Col,
} from 'react-bootstrap';
import { FaSearchPlus, FaSearchMinus, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from '../../utils/axiosConfig';
import './ProductDetailPage.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { extractYouTubeVideoID } from '../../utils/youtubeUtils'; // Ensure correct import
import useLocalizedPath from '../../hooks/useLocalizedPath';
import { withLanguage } from '../../utils/languageUrl';

const ProductDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const localizedPath = useLocalizedPath();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const initialTab = query.get('tab') || 'details';

  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [zoomScale, setZoomScale] = useState(1); // Zoom scale for image

  // Helpers to build stable translation keys from product titles
  const normalizeToKey = (text) =>
    (text || '')
      .toLowerCase()
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/ı/g, 'i')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const getProductKey = (p) => p?.translationKey || p?._id || normalizeToKey(p?.title);

  // Memoize translation values to be reactive to language changes
  // Using i18n.language ensures reactivity when language changes
  const translatedTitle = useMemo(() => {
    if (!product) return '';
    const key = getProductKey(product);
    const fullKey = `product_items.${key}.title`;
    const val = t(fullKey);
    return val === fullKey ? product.title : val;
  }, [product, t, i18n.language, currentLanguage]);

  const translatedDescription = useMemo(() => {
    if (!product) return '';
    const key = getProductKey(product);
    const fullKey = `product_items.${key}.description`;
    const val = t(fullKey);
    return val === fullKey ? product.description : val;
  }, [product, t, i18n.language, currentLanguage]);

  const translatedContent = useMemo(() => {
    if (!product) return [];
    const key = getProductKey(product);
    const fullKey = `product_items.${key}.content`;
    const arr = t(fullKey, { returnObjects: true });
    return Array.isArray(arr) && arr.length > 0 && arr[0] !== fullKey ? arr : (product.content || []);
  }, [product, t, i18n.language, currentLanguage]);

  const thumbnails = useMemo(
    () =>
      product?.images.filter((url) =>
        url.toLowerCase().includes('-320w.png')
      ) || [],
    [product]
  );

  // Memoize MSDS URL to be reactive to language changes
  // Must be before early returns to follow React Hooks rules
  const msdsUrl = useMemo(() => {
    if (!product) return null;
    return currentLanguage === 'en' ? (product.msdsEn || product.msds) : product.msds;
  }, [product, currentLanguage]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setProduct(null);
      try {
        let productData;
        
        if (currentLanguage === 'en') {
          // Try to fetch from English products first
          try {
            const englishResponse = await axios.get(`/api/english-products/${id}`);
            productData = englishResponse.data;
          } catch (englishError) {
            // If not found in English products, try Turkish products (which have translations)
            try {
              const turkishResponse = await axios.get(`/api/products/${id}`);
              productData = turkishResponse.data;
              // Only show if it has English translation
              if (!productData.hasEnglishTranslation) {
                throw new Error('Product not found in English');
              }
            } catch (turkishError) {
              throw new Error('Product not found in either language');
            }
          }
        } else {
          try {
            const response = await axios.get(`/api/products/${id}`);
            productData = response.data;
          } catch (turkishError) {
            // Old/shared English-only IDs cannot resolve in the Turkish collection.
            const englishResponse = await axios.get(`/api/english-products/${id}`);
            productData = englishResponse.data;
            navigate(
              withLanguage(
                `${window.location.pathname}${window.location.search}${window.location.hash}`,
                'en'
              ),
              { replace: true }
            );
          }
        }
        
        setProduct(productData);
        const initialMainImage =
          productData.images.find((url) =>
            url.toLowerCase().includes('-1280w.png')
          ) || productData.images[0];
        setMainImage(initialMainImage);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [
    currentLanguage,
    id,
    navigate,
  ]);

  const handleTabSelect = (selectedKey) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', selectedKey);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleImageClick = () => {
    setShowZoomModal(true);
  };

  const handleZoomModalClose = () => {
    setShowZoomModal(false);
    setZoomScale(1); // Reset zoom scale on close
  };

  const zoomIn = () =>
    setZoomScale((prev) => Math.min(prev + 0.25, 3)); // Max zoom level
  const zoomOut = () =>
    setZoomScale((prev) => Math.max(prev - 0.25, 1)); // Min zoom level

  if (loading) {
    return <div className="loading-spinner">{t('product_detail.loading')}</div>;
  }

  if (!product) {
    return <div className="error-message">{t('product_detail.not_found')}</div>;
  }

  const {
    title,
    description,
    content = [],
    weight = [],
    packSize = [],
    msds,
    msdsEn,
    tds,
    youtubeLink,
    category,
  } = product;

  // Define the category mapping for display names
  const categoryDisplayNames = {
    'bina-bakimi': t('products.categories.building_maintenance'),
    'endustriyel-kimya': t('products.categories.industrial_chemistry'),
    'kisisel-hijyen': t('products.categories.personal_hygiene'),
    'camasir': t('products.categories.laundry'),
    'dezenfektanlar-ve-antiseptikler': t('products.categories.disinfectants'),
    'gida-hijyeni': t('products.categories.food_hygiene'),
    'ozel-urunler': t('products.categories.special_products'),
    'mutfak': t('products.categories.kitchen'),
  };

  const displayCategory = categoryDisplayNames[category] || category;

  // Translations and MSDS URL are now memoized and reactive to language changes above

  // Extract YouTube Video ID
  const videoId = extractYouTubeVideoID(youtubeLink);
  const hasDemo = videoId !== null;
  const videoSrc = hasDemo
    ? `https://www.youtube.com/embed/${videoId}`
    : '';

  // Debugging: Log the extracted video ID and src
  console.log('YouTube Video ID:', videoId);
  console.log('Video Source:', videoSrc);

  return (
    <Container fluid className="product-detail-page">
      {/* Breadcrumb */}
      <Breadcrumb className="custom-breadcrumb">
        <Breadcrumb.Item as={Link} to={localizedPath('/')}>
          {t('product_detail.home')}
        </Breadcrumb.Item>
        <Breadcrumb.Item
          as={Link}
          to={localizedPath(`/products?category=${category}`)}
        >
          {displayCategory}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{translatedTitle}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Main Content Container */}
      <Row className="content-container">
        {/* Thumbnail Sidebar */}
        <Col xs={12} md={2} className="thumbnail-sidebar">
          {thumbnails.map((thumb, idx) => (
            <div
              key={idx}
              className={`thumbnail-wrapper ${
                mainImage === thumb.replace('-320w.png', '-1280w.png')
                  ? 'selected'
                  : ''
              }`}
              onClick={() =>
                setMainImage(
                  thumb.replace('-320w.png', '-1280w.png')
                )
              }
            >
              <Image
                src={thumb} // Full URL already provided by backend
                alt={`${translatedTitle} Thumbnail ${idx + 1}`}
                className="thumbnail-image"
                loading="lazy"
              />
            </div>
          ))}
        </Col>

        {/* Main Image */}
        <Col xs={12} md={5} className="main-image-container">
          <div onClick={handleImageClick}>
            <Image src={mainImage} alt={translatedTitle} className="main-image" />
          </div>
        </Col>

        {/* Product Information */}
        <Col xs={12} md={5} className="product-info-section">
          <p className="product-category">{displayCategory}</p>
          <h1 className="product-detail-title">{translatedTitle}</h1>
          <p className="product-description">{translatedDescription}</p>

          {/* Tabs for Details */}
          <Tab.Container
            activeKey={initialTab}
            onSelect={handleTabSelect}
          >
            <Nav className="product-tabs">
              <Nav.Item>
                <Nav.Link eventKey="details">{t('product_detail.details')}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="paket-boyutu">
                  {t('product_detail.package_size')}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="downloads">{t('product_detail.downloads')}</Nav.Link>
              </Nav.Item>
              {hasDemo && (
                <Nav.Item>
                  <Nav.Link eventKey="demo">{t('product_detail.demo')}</Nav.Link>
                </Nav.Item>
              )}
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="details">
                <ul className="product-details">
                  {translatedContent.map((detail, i) => (
                    <li key={i}>
                      {String(detail).replace(/^-+\s*/, '')}
                    </li>
                  ))}
                </ul>
              </Tab.Pane>
              <Tab.Pane eventKey="paket-boyutu">
                <div className="packet-sizes">
                  {weight.map((w, i) => (
                    <div key={i} className="packet-card">
                      <div className="packet-weight">
                        <strong>{t('product_detail.weight')}</strong> {w}
                      </div>
                      <div className="packet-dimensions">
                        <strong>{t('product_detail.package_dimensions')}</strong> {packSize[i]}
                      </div>
                    </div>
                  ))}
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="downloads">
                <div className="download-buttons">
                  {msdsUrl && (
                    <a
                      href={msdsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-button"
                    >
                      <i className="fas fa-file-download"></i> {t('product_detail.msds_download')}
                    </a>
                  )}
                  {tds && (
                    <a
                      href={tds} // Full URL provided by backend
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-button"
                    >
                      <i className="fas fa-file-download"></i> {t('product_detail.tds_download')}
                    </a>
                  )}
                </div>
              </Tab.Pane>
              {hasDemo && (
                <Tab.Pane eventKey="demo">
                  <div className="video-container">
                    <div className="video-responsive">
                      <iframe
                        src={videoSrc}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      ></iframe>
                    </div>
                  </div>
                </Tab.Pane>
              )}
            </Tab.Content>
          </Tab.Container>
        </Col>
      </Row>

      {/* Zoom Modal */}
      <Modal
        show={showZoomModal}
        onHide={handleZoomModalClose}
        size="lg"
        centered
      >
        <Modal.Body
          className="text-center"
          style={{ position: 'relative' }}
        >
          <img
            src={mainImage} // Full URL provided by backend
            alt={translatedTitle}
            style={{
              transform: `scale(${zoomScale})`,
              transition: 'transform 0.3s',
            }}
            className="img-fluid"
          />
          {/* Zoom Controls - Stacked vertically on the right */}
          <Button
            variant="light"
            className="zoom-control zoom-in"
            onClick={zoomIn}
          >
            <FaSearchPlus color="#007cfb" />
          </Button>
          <Button
            variant="light"
            className="zoom-control zoom-out"
            onClick={zoomOut}
          >
            <FaSearchMinus color="#007cfb" />
          </Button>
          <Button
            variant="light"
            className="zoom-control close-zoom"
            onClick={handleZoomModalClose}
          >
            <FaTimes color="#007cfb" />
          </Button>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ProductDetailPage;
